import { Controller } from "@hotwired/stimulus"

// The Self Sign-Up Builder's live side: reflects placement changes ahead of
// any save. Keeps the "N in portal" counter and the cards' placement notes
// in sync, and drives the preview pane — the checkout tab's iframe reloads
// the real wizard with the unsaved placements as params, while the
// deliberately-schematic consequence tabs toggle their mock pieces.
export default class extends Controller {
  static targets = [
    "adminPending",
    "adminSettled",
    "iframe",
    "panel",
    "portalChip",
    "portalCount",
    "portalEmpty",
    "portalModal",
    "tab",
  ]

  static values = { previewPath: String }

  connect() {
    this.syncPreview()
  }

  // The current (unsaved) placements, read off the form's checked radios
  // (named self_sign_up_configuration[<step>_placement]).
  placements() {
    const out = {}

    this.element
      .querySelectorAll("input[type=radio]:checked")
      .forEach((radio) => {
        const step = radio.name.match(/\[(\w+)_placement\]/)?.[1]
        if (step) out[step] = radio.value
      })

    return out
  }

  refresh(event) {
    const count = this.element.querySelectorAll(
      "input[type=radio][value=customer_portal]:checked"
    ).length

    this.portalCountTarget.textContent = `${count} in portal`

    const radio = event.target
    radio
      .closest("[data-controller~=collapsible]")
      ?.querySelectorAll("[data-placement-note]")
      .forEach((note) => {
        note.classList.toggle("hidden", note.dataset.placementNote !== radio.value)
      })

    this.syncPreview()
  }

  showTab(event) {
    const tab = event.currentTarget.dataset.tab

    this.tabTargets.forEach((button) => {
      const active = button.dataset.tab === tab
      button.classList.toggle("bg-blue-700", active)
      button.classList.toggle("text-white", active)
      button.classList.toggle("text-slate-600", !active)
    })

    this.panelTargets.forEach((panel) => {
      panel.classList.toggle("hidden", panel.dataset.panel !== tab)
    })
  }

  syncPreview() {
    const placements = this.placements()

    if (this.hasIframeTarget && this.previewPathValue) {
      const url = new URL(this.previewPathValue, window.location.origin)
      url.searchParams.set("builder_preview", "1")
      Object.entries(placements).forEach(([step, value]) => {
        url.searchParams.set(`placements[${step}]`, value)
      })

      // Always set: the admin may have navigated inside the iframe, so an
      // unchanged attribute does not mean an unchanged page — and a radio
      // click deserves a visible reload either way.
      this.iframeTarget.setAttribute("src", url.pathname + "?" + url.searchParams.toString())
    }

    const portalSteps = Object.entries(placements)
      .filter(([, value]) => value === "customer_portal")
      .map(([step]) => step)

    this.portalChipTargets.forEach((chip) => {
      chip.classList.toggle("hidden", !portalSteps.includes(chip.dataset.step))
    })

    const anyPortal = portalSteps.length > 0
    if (this.hasPortalModalTarget) this.portalModalTarget.classList.toggle("hidden", !anyPortal)
    if (this.hasPortalEmptyTarget) this.portalEmptyTarget.classList.toggle("hidden", anyPortal)
    if (this.hasAdminPendingTarget) this.adminPendingTarget.classList.toggle("hidden", !anyPortal)
    if (this.hasAdminSettledTarget) this.adminSettledTarget.classList.toggle("hidden", anyPortal)
  }
}
